import { z } from 'zod';

export const OrganizationStatuses = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const;

export const OrderAcceptanceModes = {
  OPEN: 'open',
  ADMIN_ONLY: 'admin_only',
  CLOSED: 'closed',
} as const;

export type OrderAcceptanceMode =
  (typeof OrderAcceptanceModes)[keyof typeof OrderAcceptanceModes];

export const organizationZ = z.object({
  name: z.string().min(1, 'Name is required'),
  status: z.enum(OrganizationStatuses),
  timezone: z.string().default('America/Denver'), // IANA timezone identifier
  _id: z.string().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  fromEmail: z.email('Invalid email address'),
  toEmail: z.email('Invalid email address'),
  // Organization-specific pricing configuration
  pricePerTonInCents: z.number().min(0, 'Price per ton must be greater than 0'),
  minimumOrderWeightInTons: z.number().min(0, 'Minimum order tonnage must be greater than 0'),
  adminBccEmail: z.email('Invalid email format').optional(),
  stripeConnectAccountId: z.string().optional(),
  isPlatform: z.boolean().optional(), // True if this org is the platform account, false for connected accounts
  defaultRentalDays: z.number().min(1, 'Default rental days must be 1 or greater').default(7),
  // Organization-specific review links
  googleReviewUrl: z.url('Invalid URL format').optional(),
  // Tax collection configuration
  collectTaxes: z.boolean().default(true),
  // Distance-based pricing configuration
  minimumDistanceThresholdInMiles: z
    .number()
    .min(0, 'Minimum distance threshold must be 0 or greater')
    .default(0),
  distanceIncrementInMiles: z.number().min(0, 'Distance increment must be 0 or greater').default(0),
  surchargePerIncrementInCents: z
    .number()
    .min(0, 'Surcharge per increment must be 0 or greater')
    .default(0),
  minimumSurchargeInCents: z.number().min(0, 'Minimum surcharge must be 0 or greater').default(0),
  // SMS-related configuration
  sameDayPickupCutoffHour: z
    .number()
    .min(0, 'Cutoff hour must be between 0 and 23')
    .max(23, 'Cutoff hour must be between 0 and 23')
    .optional(),
  // Same-day delivery fee configuration
  sameDayAddOnId: z.string().optional(),
  // Hotjar survey configuration
  hotjarSurveyEventName: z.string().optional(),
  reminderCutoffHour: z
    .number()
    .min(0, 'Reminder cutoff hour must be between 0 and 23')
    .max(23, 'Reminder cutoff hour must be between 0 and 23')
    .optional(),
  // Order acceptance mode: controls whether new orders can be created for this org
  orderAcceptanceMode: z
    .enum([OrderAcceptanceModes.OPEN, OrderAcceptanceModes.ADMIN_ONLY, OrderAcceptanceModes.CLOSED])
    .default(OrderAcceptanceModes.OPEN),
});
